import React, { useState, useEffect } from "react";
import { Code2, X, Sparkles, Rocket } from "lucide-react";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../../../config/firebaseConfig";
import { toast } from "react-toastify";
import { WORKSPACE_OPTIONS } from "./AddChildModal";

const SelectWorkspaceModal = ({
  isOpen,
  onClose,
  child,
  onWorkspaceSet,
}) => {
  const [selectedWorkspace, setSelectedWorkspace] = useState("scratch_jr");
  const [juniorSubMode, setJuniorSubMode] = useState("basic");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Determine intelligent default based on child's class/grade
  useEffect(() => {
    if (child) {
      const gradeStr = (child.grade || child.studentClass || child.class || "").toString();
      const numMatch = gradeStr.match(/\d+/);
      const gradeNum = numMatch ? parseInt(numMatch[0], 10) : 1;

      if (child.workspace) {
        setSelectedWorkspace(child.workspace);
      } else if (gradeNum <= 3) {
        setSelectedWorkspace("scratch_jr");
      } else if (gradeNum >= 9) {
        setSelectedWorkspace("python");
      } else {
        setSelectedWorkspace("scratch");
      }

      setJuniorSubMode(
        child.juniorSubMode || (gradeNum === 1 ? "basic" : "advanced")
      );
      setError("");
    }
  }, [child, isOpen]);

  if (!isOpen || !child) return null;

  const handleConfirm = async (e) => {
    e?.preventDefault();
    if (!selectedWorkspace) {
      setError("Please select a workspace option.");
      return;
    }

    const childId = child.id || child.uid;
    if (!childId) {
      setError("Child record not found. Please try again.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const studentRef = doc(db, "students", childId);
      const updateData = {
        workspace: selectedWorkspace,
        juniorSubMode: selectedWorkspace === "scratch_jr" ? juniorSubMode : null,
        updatedAt: serverTimestamp(),
      };
      await updateDoc(studentRef, updateData);

      const updatedChild = {
        ...child,
        ...updateData,
      };

      toast.success(
        `Primary workspace set to ${
          selectedWorkspace === "python"
            ? "Practido Code"
            : selectedWorkspace === "scratch_jr"
            ? `Junior Blocks (${juniorSubMode === "advanced" ? "Advanced" : "Basic"})`
            : "Practido Blocks"
        } for ${child.name}!`
      );

      if (onWorkspaceSet) {
        onWorkspaceSet(updatedChild);
      }
      onClose();
    } catch (err) {
      console.error("Error setting default workspace:", err);
      setError(err.message || "Failed to set default workspace. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl space-y-5 animate-in zoom-in-95 duration-200 border border-slate-100 font-sans text-slate-800">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-purple-100 text-purple-700 flex items-center justify-center font-bold">
              <Rocket className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-900">
                Choose your Workspace
              </h3>
              <p className="text-xs text-slate-500 font-medium">
                Select the default studio for <strong className="text-slate-800">{child.name}</strong>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-colors cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-2xl text-xs font-bold">
            {error}
          </div>
        )}

        <div className="space-y-4">
          {/* Workspace Options (Matches AddChildModal UI) */}
          <div className="space-y-1.5 text-left">
            <label className="text-xs font-extrabold text-slate-700 flex items-center gap-1.5">
              <Code2 className="w-3.5 h-3.5 text-purple-600" />
              <span>Choose your Workspace *</span>
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 pt-1">
              {WORKSPACE_OPTIONS.map((opt) => {
                const isSelected = selectedWorkspace === opt.value;
                const isDisabled = opt.disabled;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    disabled={isDisabled}
                    onClick={() => {
                      if (!isDisabled) setSelectedWorkspace(opt.value);
                    }}
                    className={`p-3 rounded-2xl border text-left flex flex-col justify-between gap-2 transition-all ${
                      isDisabled
                        ? "opacity-60 bg-slate-50 border-slate-200 cursor-not-allowed"
                        : isSelected
                        ? "border-purple-600 bg-purple-50/80 ring-2 ring-purple-200 shadow-xs cursor-pointer"
                        : "border-slate-200 hover:border-purple-300 hover:bg-slate-50 cursor-pointer"
                    }`}
                  >
                    <div className="flex items-center justify-between w-full">
                      <span className="text-xl leading-none">{opt.icon}</span>
                      {opt.badge && (
                        <span className="bg-amber-100 text-amber-800 text-[9px] font-black px-2 py-0.5 rounded-full">
                          {opt.badge}
                        </span>
                      )}
                    </div>
                    <div className="space-y-0.5 min-w-0 flex-1">
                      <h4
                        className={`text-xs font-black leading-tight ${
                          isSelected ? "text-purple-900" : "text-slate-800"
                        }`}
                      >
                        {opt.label}
                      </h4>
                      <p className="text-[10px] text-slate-500 line-clamp-2 leading-tight font-medium">
                        {opt.desc}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Sub-mode selector for Junior Blocks */}
            {selectedWorkspace === "scratch_jr" && (
              <div className="mt-2.5 p-3.5 bg-gradient-to-br from-purple-50/90 to-indigo-50/70 border border-purple-200/90 rounded-2xl space-y-2.5 animate-in fade-in zoom-in-98 duration-200">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-black text-purple-900 flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-purple-600" />
                    Junior Blocks Level
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {/* Basic Mode */}
                  <button
                    type="button"
                    onClick={() => setJuniorSubMode("basic")}
                    className={`p-2.5 rounded-xl border text-left flex items-start gap-2.5 transition-all cursor-pointer ${
                      juniorSubMode === "basic"
                        ? "bg-white border-purple-600 ring-2 ring-purple-300 shadow-xs"
                        : "bg-white/70 border-purple-100/80 hover:bg-white hover:border-purple-300"
                    }`}
                  >
                    <span className="text-xl">🌱</span>
                    <div className="min-w-0 flex-1">
                      <span className="text-xs font-black text-slate-800">Basic</span>
                      <p className="text-[10px] text-slate-500 font-medium leading-tight mt-0.5">
                        Pure picture blocks, ideal for age 5-7
                      </p>
                    </div>
                  </button>

                  {/* Advanced Mode */}
                  <button
                    type="button"
                    onClick={() => setJuniorSubMode("advanced")}
                    className={`p-2.5 rounded-xl border text-left flex items-start gap-2.5 transition-all cursor-pointer ${
                      juniorSubMode === "advanced"
                        ? "bg-white border-purple-600 ring-2 ring-purple-300 shadow-xs"
                        : "bg-white/70 border-purple-100/80 hover:bg-white hover:border-purple-300"
                    }`}
                  >
                    <span className="text-xl">🚀</span>
                    <div className="min-w-0 flex-1">
                      <span className="text-xs font-black text-slate-800">Advanced</span>
                      <p className="text-[10px] text-slate-500 font-medium leading-tight mt-0.5">
                        Adds parameters, operators & variables, ideal for age 7-10
                      </p>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="pt-3 flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="w-1/3 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-extrabold text-xs rounded-2xl transition cursor-pointer"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleConfirm}
              disabled={loading}
              className="w-2/3 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-extrabold text-xs rounded-2xl shadow-lg shadow-purple-500/20 flex items-center justify-center gap-2 transition cursor-pointer disabled:opacity-50"
            >
              {loading ? (
                <span>Setting Default...</span>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  <span>Set as Default &amp; Open Studio 🚀</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectWorkspaceModal;
